import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trophy, Target, Clock, Keyboard, RotateCcw, ListChecks, AlertCircle, TrendingUp, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TypingResult = () => {
  const { passageId } = useParams();
  const [params] = useSearchParams();
  const isGuest = params.get('guest') === '1';
  const navigate = useNavigate();

  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Typing Test Result — Vidya365';
    // 1. Load latest result from sessionStorage (set by TypingTest.jsx)
    try {
      const raw = sessionStorage.getItem(`typing-result:${passageId}`);
      if (raw) setResult(JSON.parse(raw));
    } catch (e) { /* noop */ }

    // 2. Fetch history for logged-in students
    const token = localStorage.getItem('studentToken');
    if (token && !isGuest) {
      fetch(`${API_URL}/api/typing/attempts/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setHistory(data || []))
        .catch(() => setHistory([]))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [passageId, isGuest]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <AlertCircle className="mx-auto text-red-600 mb-3" size={48} />
          <p className="text-gray-700 mb-4">No recent result found. Please retake the test.</p>
          <Link to={`/typing-test/${passageId}`}><Button data-testid="retake-btn">Retake Test</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const passed = result.passed ?? (result.wpm >= (result.target_wpm || 35));

  // Build a clean trend for chart (last 10 attempts, oldest → newest)
  const trend = history
    .slice()
    .reverse()
    .slice(-10)
    .map((h, i) => ({
      idx: i + 1,
      wpm: h.wpm,
      accuracy: h.accuracy,
      date: (h.submitted_at || '').slice(0, 10),
    }));

  // Personal bests
  const bestWpm = history.length ? Math.max(...history.map((h) => h.wpm)) : result.wpm;
  const avgWpm = history.length ? history.reduce((s, h) => s + h.wpm, 0) / history.length : result.wpm;
  const avgAcc = history.length ? history.reduce((s, h) => s + h.accuracy, 0) / history.length : result.accuracy;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Hero Result */}
        <Card className={`mb-6 border-2 ${passed ? 'border-green-400 bg-gradient-to-br from-green-50 to-white' : 'border-amber-400 bg-gradient-to-br from-amber-50 to-white'}`}>
          <CardContent className="p-6 text-center">
            <Trophy className={`mx-auto mb-2 ${passed ? 'text-green-600' : 'text-amber-500'}`} size={48} />
            <h1 className="text-2xl font-bold mb-1" data-testid="result-headline">
              {passed ? 'Great Job! You Passed' : 'Keep Practicing!'}
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              {result.passage_title} • {result.exam_name} • {result.language}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
              <ResultStat icon={<Keyboard size={18} />} label="WPM" value={result.wpm.toFixed(1)} highlight testid="result-wpm" />
              <ResultStat icon={<Target size={18} />} label="Accuracy" value={`${result.accuracy.toFixed(1)}%`} testid="result-accuracy" />
              <ResultStat icon={<Clock size={18} />} label="Time Taken" value={`${Math.round(result.duration_seconds / 60)}m ${result.duration_seconds % 60}s`} />
              <ResultStat icon={<ListChecks size={18} />} label="Target" value={`${result.target_wpm || 35} WPM`} />
            </div>
          </CardContent>
        </Card>

        {/* Detail grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <DetailCard label="Correct Chars" value={result.correct_chars} color="text-green-600" />
          <DetailCard label="Wrong Chars" value={result.wrong_chars} color="text-red-600" />
          <DetailCard label="Total Typed" value={result.total_typed} color="text-gray-700" />
        </div>

        {/* Guest CTA */}
        {(isGuest || result.guest) && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-red-900">Result not saved to your profile</p>
                <p className="text-sm text-red-700">Login to track your history, see WPM trends and unlock the dashboard.</p>
              </div>
              <div className="flex gap-2">
                <Link to="/student/login"><Button data-testid="login-cta" className="bg-red-600 hover:bg-red-700">Login</Button></Link>
                <Link to="/student/register"><Button variant="outline" data-testid="register-cta">Register</Button></Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trend chart for logged in students with history */}
        {history.length > 0 && (
          <Card className="mb-6" data-testid="history-card">
            <CardContent className="p-5">
              <h2 className="text-lg font-bold mb-3 flex items-center">
                <TrendingUp className="mr-2 text-red-600" size={20} /> Your Progress (Last 10 Attempts)
              </h2>
              <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                <DetailCard label="Best WPM" value={bestWpm.toFixed(1)} color="text-red-600" />
                <DetailCard label="Average WPM" value={avgWpm.toFixed(1)} color="text-blue-600" />
                <DetailCard label="Average Accuracy" value={`${avgAcc.toFixed(1)}%`} color="text-green-600" />
              </div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="idx" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="wpm" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} name="WPM" />
                    <Line type="monotone" dataKey="accuracy" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} name="Accuracy" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent history list */}
        {history.length > 0 && (
          <Card className="mb-6">
            <CardContent className="p-5">
              <h2 className="text-lg font-bold mb-3">Recent Attempts</h2>
              <div className="divide-y">
                {history.slice(0, 10).map((h) => (
                  <div key={h.id} className="py-3 flex items-center justify-between gap-3" data-testid={`history-row-${h.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{h.passage_title}</p>
                      <p className="text-xs text-gray-500">{h.exam_name} • {h.language} • {(h.submitted_at || '').slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge className={h.passed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {h.wpm.toFixed(1)} WPM
                      </Badge>
                      <span className="text-gray-600">{h.accuracy.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 justify-center">
          <Button data-testid="retake-test" onClick={() => navigate(`/typing-test/${passageId}`)} className="bg-red-600 hover:bg-red-700">
            <RotateCcw size={16} className="mr-2" /> Retake This Test
          </Button>
          <Link to="/typing-test">
            <Button variant="outline" data-testid="more-tests">Take More Tests</Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ResultStat = ({ icon, label, value, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`p-3 rounded-lg border ${highlight ? 'bg-red-600 text-white border-red-700' : 'bg-white border-gray-200'}`}
  >
    <div className={`flex items-center justify-center gap-1 text-xs ${highlight ? 'text-red-100' : 'text-gray-500'}`}>
      {icon}<span>{label}</span>
    </div>
    <p className="text-xl font-bold mt-1">{value}</p>
  </div>
);

const DetailCard = ({ label, value, color = 'text-gray-700' }) => (
  <div className="bg-white border rounded-lg p-3 text-center">
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

export default TypingResult;
